import { useState } from "react";
import { api, apiError, eur } from "@/lib/api";
import { useI18n } from "@/i18n";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Wrench, AlertTriangle, Store } from "lucide-react";
import { toast } from "sonner";

const Row = ({ label, value }) => (
  <div className="flex justify-between gap-4 text-sm"><span className="text-muted-foreground">{label}</span><span className="font-mono">{value}</span></div>
);

export const OrderCorrection = ({ order, onDone }) => {
  const { t } = useI18n();
  const [open, setOpen] = useState(false);
  const [chan, setChan] = useState(order.channel === "Shopify" ? "B2B" : "Shopify");
  const [chanReason, setChanReason] = useState("");
  const [chanPrev, setChanPrev] = useState(null);
  const [rev, setRev] = useState(order.revenue_excluded ? "relevant" : "excluded");
  const [revReason, setRevReason] = useState("");
  const [revPrev, setRevPrev] = useState(null);
  const [busy, setBusy] = useState(false);
  const [onum, setOnum] = useState(order.order_number || "");
  const [onumReason, setOnumReason] = useState("");

  const call = async (url, bodyKey, body, apply) => {
    setBusy(true);
    try {
      const r = await api.post(`${url}?apply=${apply}`, body);
      return r.data;
    } catch (e) { toast.error(apiError(e)); return null; } finally { setBusy(false); }
  };

  const previewChannel = async () => setChanPrev(await call(`/orders/${order.id}/correct-channel`, "c", { channel: chan, reason: chanReason }, false));
  const applyChannel = async () => {
    const r = await call(`/orders/${order.id}/correct-channel`, "c", { channel: chan, reason: chanReason }, true);
    if (r) { toast.success(r.changes ? t("oc.done") : t("oc.noChange")); setChanPrev(null); setOpen(false); onDone?.(); }
  };
  const previewRevenue = async () => setRevPrev(await call(`/orders/${order.id}/correct-revenue-relevance`, "r", { revenue_relevant: rev === "relevant", reason: revReason }, false));
  const applyRevenue = async () => {
    const r = await call(`/orders/${order.id}/correct-revenue-relevance`, "r", { revenue_relevant: rev === "relevant", reason: revReason }, true);
    if (r) { toast.success(r.financial_impact || r.changes ? t("oc.done") : t("oc.noChange")); setRevPrev(null); setOpen(false); onDone?.(); }
  };
  const applyNumber = async () => {
    const nn = onum.trim();
    if (!nn || nn === order.order_number) { toast.message(t("oc.noChange")); return; }
    const r = await call(`/orders/${order.id}/correct-order-number`, "n", { order_number: nn, reason: onumReason }, true);
    if (r) { toast.success(t("oc.done")); setOpen(false); onDone?.(); }
  };
  const markMesse = async (flag) => {
    setBusy(true);
    try {
      await api.post(`/orders/${order.id}/messe`, { messe: flag });
      toast.success(t("oc.done")); setOpen(false); onDone?.();
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };

  return (
    <>
      <Button variant="outline" size="sm" className="border-amber-500/50 text-amber-700" onClick={() => setOpen(true)} data-testid="order-correct-btn">
        <Wrench className="h-4 w-4 mr-1.5" /> {t("oc.correct")}
      </Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg" data-testid="order-correct-dialog">
          <DialogHeader>
            <DialogTitle>{t("oc.correct")} · {order.order_number}</DialogTitle>
            <DialogDescription>{t("oc.onlySuper")}</DialogDescription>
          </DialogHeader>

          <div className="rounded-md border border-border p-3 space-y-2">
            <div className="font-medium text-sm">{t("oc.orderNumber")}</div>
            <div className="flex items-center gap-2">
              <Input className="w-40 font-mono" value={onum} onChange={(e) => setOnum(e.target.value)} data-testid="oc-number-input" />
              <Input placeholder={t("oc.reason")} value={onumReason} onChange={(e) => setOnumReason(e.target.value)} data-testid="oc-number-reason" />
            </div>
            <p className="text-xs text-muted-foreground">{t("oc.orderNumberHint")}</p>
            <div className="flex gap-2">
              <Button size="sm" disabled={busy} onClick={applyNumber} data-testid="oc-number-apply-btn">{t("oc.apply")}</Button>
            </div>
          </div>

          <div className="rounded-md border border-border p-3 space-y-2">
            <div className="font-medium text-sm flex items-center gap-1.5"><Store className="h-4 w-4" /> {t("oc.messe")}</div>
            <p className="text-xs text-muted-foreground">{order.messe ? t("oc.messeStateOn") : t("oc.messeStateOff")} · {t("oc.messeHint")}</p>
            <div className="flex gap-2">
              {!order.messe
                ? <Button size="sm" variant="outline" disabled={busy || order.order_status !== "entwurf"} title={order.order_status !== "entwurf" ? t("od.messeBlocked") : ""} onClick={() => markMesse(true)} data-testid="oc-messe-mark-btn"><Store className="h-4 w-4 mr-1.5" /> {t("od.markMesse")}</Button>
                : <Button size="sm" variant="outline" disabled={busy || order.order_status !== "entwurf"} onClick={() => markMesse(false)} data-testid="oc-messe-unmark-btn"><Store className="h-4 w-4 mr-1.5" /> {t("od.unmarkMesse")}</Button>}
            </div>
          </div>

          <div className="rounded-md border border-border p-3 space-y-2">
            <div className="font-medium text-sm">{t("oc.channel")}</div>
            <div className="flex items-center gap-2">
              <Select value={chan} onValueChange={setChan}>
                <SelectTrigger className="w-40" data-testid="oc-channel-select"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="Shopify">Shopify</SelectItem><SelectItem value="B2B">B2B</SelectItem></SelectContent>
              </Select>
              <Input placeholder={t("oc.reason")} value={chanReason} onChange={(e) => setChanReason(e.target.value)} data-testid="oc-channel-reason" />
            </div>
            {chanPrev && (
              <div className="rounded bg-muted/50 p-2 space-y-1" data-testid="oc-channel-preview">
                <Row label={t("ord.colChannel")} value={`${chanPrev.before.channel} → ${chanPrev.after.channel}`} />
                <Row label="Events" value={chanPrev.affected_financial_events.length} />
              </div>
            )}
            <div className="flex gap-2">
              <Button variant="outline" size="sm" disabled={busy} onClick={previewChannel} data-testid="oc-channel-preview-btn">{t("oc.preview")}</Button>
              <Button size="sm" disabled={busy || !chanPrev} onClick={applyChannel} data-testid="oc-channel-apply-btn">{t("oc.apply")}</Button>
            </div>
          </div>

          <div className="rounded-md border border-border p-3 space-y-2">
            <div className="font-medium text-sm">{t("oc.revenue")}</div>
            <div className="flex items-center gap-2">
              <Select value={rev} onValueChange={setRev}>
                <SelectTrigger className="w-56" data-testid="oc-revenue-select"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="relevant">{t("oc.relevant")}</SelectItem><SelectItem value="excluded">{t("oc.excluded")}</SelectItem></SelectContent>
              </Select>
              <Input placeholder={t("oc.reason")} value={revReason} onChange={(e) => setRevReason(e.target.value)} data-testid="oc-revenue-reason" />
            </div>
            {revPrev && (
              <div className="rounded bg-muted/50 p-2 space-y-1" data-testid="oc-revenue-preview">
                <Row label={t("common.revenueNet")} value={`${eur(revPrev.before.ledger_net)} → ${eur(revPrev.after.ledger_net)}`} />
                <Row label={t("oc.impact")} value={revPrev.financial_impact ? eur(revPrev.correction_amount) : "—"} />
                {revPrev.financial_impact && <div className="flex items-center gap-1 text-xs text-amber-700"><AlertTriangle className="h-3.5 w-3.5" /> {t("oc.financeWarn")}</div>}
              </div>
            )}
            <div className="flex gap-2">
              <Button variant="outline" size="sm" disabled={busy} onClick={previewRevenue} data-testid="oc-revenue-preview-btn">{t("oc.preview")}</Button>
              <Button size="sm" disabled={busy || !revPrev} onClick={applyRevenue} data-testid="oc-revenue-apply-btn">{t("oc.apply")}</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default OrderCorrection;
